import { useMemo } from 'react';
import { useSessionStore } from '@/state/sessionStore';
import { useBrainStore } from '@/state/brainStore';
import { activeStudioIds, isAllStudios, type ActiveStudio } from '@/domain/studioScope';
import type { StudioId } from '@/domain/types';

export interface ActiveStudioScope {
  // The live switcher selection: a studio, 'all', or null before login.
  activeStudio: ActiveStudio | null;
  assignedStudios: StudioId[];
  // The switcher only renders (and 'all' is only offered) when the persona spans
  // 2+ studios.
  isMultiStudio: boolean;
  // True for the network-wide view (explicit 'all' or nothing picked yet).
  isAll: boolean;
}

// Shared read of the top-bar studio switcher. Every studio-scoped screen consumes
// this instead of keeping its own local dropdown state, so changing the switcher
// re-scopes the whole app.
export function useActiveStudio(): ActiveStudioScope {
  const activeStudio = useSessionStore((s) => s.activeStudio);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  return {
    activeStudio,
    assignedStudios,
    isMultiStudio: assignedStudios.length >= 2,
    isAll: isAllStudios(activeStudio),
  };
}

/**
 * branding-3: the persona's assigned studios that are open for business (an
 * archived studio drops out). What every switcher, studio select and "All
 * studios" list offers.
 */
export function useActiveStudioIds(): StudioId[] {
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const studios = useBrainStore((s) => s.studios);
  return useMemo(() => activeStudioIds(studios, assignedStudios), [studios, assignedStudios]);
}

/**
 * The studio ids a screen reads for the current switcher scope: the one picked
 * studio, or, on "All studios", the persona's active (non-archived) studios.
 */
export function useStudioScopeIds(): StudioId[] {
  const activeStudio = useSessionStore((s) => s.activeStudio);
  const active = useActiveStudioIds();
  return useMemo(
    () => (isAllStudios(activeStudio) ? active : [activeStudio as StudioId]),
    [activeStudio, active],
  );
}
